import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Event, NavigationEnd, Router, RouterEvent } from "@angular/router";
import { Subscription } from "rxjs";
import { filter, startWith } from "rxjs/operators";
import { CampaignType } from "../../../shared/enums/campaign.enums";
import { cloneWith } from "../../../shared/services/clonable";
import { BrandService } from "../../../shared/services/api/brand.service";
import { NotificationService } from "../../../shared/services/notification.service";
import {
  BRAND_CAMPAIGN_STATUS_FILTERS,
  BrandCampaign,
  BrandCampaignStatus,
  BrandCampaignStatusConfig,
} from "../../../shared/models/brandCampaign";
import { AuthenticationService } from "../../../shared/services/api/authentication.service";
import { BrandCampaignDetails } from "../../../shared/models/brandCampaignDetail";
import { BrandUrl as Url } from "../../brand.url";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";

@Component({
  selector: "app-brand-campaign-header",
  templateUrl: "./brand-campaign-header.component.html",
  styleUrl: "./brand-campaign-header.component.scss",
})
export class BrandCampaignHeaderComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public title!: string;
  @Input({ required: true }) public campaignType!: CampaignType;

  protected readonly BrandCampaignStatusActiveValue =
    BRAND_CAMPAIGN_STATUS_FILTERS[BrandCampaignStatus.Active]!.value;
  protected campaign!: BrandCampaign;
  protected campaignStatus?: BrandCampaignStatusConfig;
  protected isLastStep = false;
  protected saving = false;

  private readonly subscriptions = new Subscription();
  private campaignDetails!: BrandCampaignDetails;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly brandService: BrandService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    this.getCampaign();
    this.initLastStep();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initLastStep(): void {
    this.setLastStep();

    const routerSubscription = this.router.events
      .pipe(
        filter(
          (e: Event | RouterEvent): e is RouterEvent =>
            e instanceof NavigationEnd,
        ),
        startWith(this.router),
      )
      .subscribe(() => {
        this.setLastStep();
      });

    this.subscriptions.add(routerSubscription);
  }

  private setLastStep(): void {
    this.isLastStep = this.router.url.endsWith("/summary");
  }

  private getCampaign(): void {
    this.subscriptions.add(
      this.brandCampaignService.currentBrandCampaign$.subscribe((campaign) => {
        if (campaign) {
          this.campaign = campaign;
          this.campaignStatus =
            BRAND_CAMPAIGN_STATUS_FILTERS[campaign.campaignBrandStatus];
          this.campaignDetails = campaign.currentDetails;
        }
      }),
    );
  }

  protected async onSubmitForReview(): Promise<void> {
    const isAllGood =
      await this.brandCampaignDialogService.showCampaignSubmitCheck({
        campaign: this.campaign,
      });

    if (isAllGood) {
      this.submitForReview();
    }
  }

  private submitForReview(): void {
    this.saving = true;

    const campaign = cloneWith(this.campaign, {
      campaignBrandStatus: BrandCampaignStatus.PendingValidation,
    });

    this.brandCampaignService.updateBrandCampaign(campaign).subscribe({
      next: () => {
        this.saving = false;
        this.campaign = campaign;
        this.notificationService.success(
          "brand.campaign.campaignSubmittedSuccessfully",
        );
      },
      error: (res: HttpErrorResponse) => {
        let message = "shared.errorSavingChanges";

        switch (res.error?.error?.key) {
          case "WRONG_NUMBER_OF_ADS_IN_CAMPAIGN_ERROR":
            message = "brand.campaign.wrongNumberOfAdsError";
            break;
          case "ALREADY_INVITED_PARTNERS":
            message = "brand.campaign.campaignInvalidLandingPageType";
            break;
        }

        this.notificationService.error(message);
        this.saving = false;
      },
    });
  }

  protected async onCampaignNameEdit(): Promise<void> {
    const updatedCampaign = await this.brandCampaignDialogService.showNameEdit(
      this.campaign,
    );

    if (updatedCampaign) {
      this.title = updatedCampaign.name;
    }
  }

  protected onNextStep(): void {
    this.saving = true;

    this.brandCampaignService.updateBrandCampaign(this.campaign).subscribe({
      next: () => {
        this.updateCampaignDetails();
      },
      error: (err) => {
        const error = err.error;
        this.saving = false;
        let message = "Campaign could not be updated";
        if (error?.key === "PROJECT_NAME_EXISTS") {
          message =
            "This campaign project name has already been used. Please provide another one.";
        } else if (error?.key === "INVITATION_CODE_EXISTS") {
          message =
            "This invitation code has already been used. Please provide another one.";
        }
        this.notificationService.error(message);
      },
    });
  }

  private updateCampaignDetails(): void {
    this.brandCampaignService
      .updateBrandCampaignDetails(this.campaign, this.campaignDetails)
      .subscribe({
        next: () => {
          this.notificationService.success("Campaign saved");
          this.saving = false;
          this.goToNextStep();
        },
        error: () => {
          this.notificationService.error("Campaign could not be updated");
        },
      });
  }

  private goToNextStep(): void {
    const hasLandingOrPOS = !this.campaign.isDownloadOnly;

    const nextStepRoute = this.getNextStepRoute(
      this.router.url,
      hasLandingOrPOS,
    );

    if (nextStepRoute) {
      this.router.navigateByUrl(nextStepRoute);
    }
  }

  protected getNextStepRoute(
    url: string,
    hasLandingOrPOS: boolean,
  ): string | undefined {
    const cId = this.campaign.id;
    const bId = this.brandService.currentBrandId;

    switch (url) {
      case Url.Campaign.DetailsSettings(bId, cId): {
        return Url.Campaign.DetailsInfo(bId, cId);
      }
      case Url.Campaign.DetailsInfo(bId, cId): {
        return Url.Campaign.DetailsInvitePage(bId, cId);
      }
      case Url.Campaign.DetailsInvitePage(bId, cId): {
        return this.campaign.isBeta
          ? Url.Campaign.Publications(bId, cId)
          : Url.Campaign.Content(bId, cId);
      }
      case Url.Campaign.Content(bId, cId): {
        return Url.Campaign.AdsSettings(bId, cId);
      }
      case Url.Campaign.Ads(bId, cId): {
        return Url.Campaign.AdsChannels(bId, cId);
      }
      case Url.Campaign.AdsSettings(bId, cId): {
        return Url.Campaign.AdsChannels(bId, cId);
      }
      case Url.Campaign.AdsChannels(bId, cId): {
        return hasLandingOrPOS
          ? Url.Campaign.LandingPage(bId, cId)
          : Url.Campaign.ContentCalendar(bId, cId);
      }
      case Url.Campaign.LandingPage(bId, cId): {
        return Url.Campaign.ContentCalendar(bId, cId);
      }
      case Url.Campaign.ContentCalendar(bId, cId): {
        return Url.Campaign.InvitePartners(bId, cId);
      }
      case Url.Campaign.InvitePartners(bId, cId): {
        return hasLandingOrPOS
          ? Url.Campaign.Pos(bId, cId)
          : Url.Campaign.Summary(bId, cId);
      }
      case Url.Campaign.Pos(bId, cId): {
        return Url.Campaign.Summary(bId, cId);
      }
    }

    return undefined;
  }

  protected shouldDisplayCalendarLegend(): boolean {
    return (
      this.authService.isRoleAdmin() && this.router.url.endsWith("/calendar")
    );
  }
}
